<section class="service-page">
  <div class="row pad">
    <div class="service-page__inner">
      <div class="text-muted">7 September 2026</div>
      <h1>MARI Privacy Notice</h1>

      <p>
        We are committed to protecting the personal data of those individuals
        whose information we process.
      </p>

      <h2>Who are we?</h2>

      <p>
        We are MARI Group, which includes AE EventsCo Holdings LLC and
        affiliated companies (collectively, “MARI Group,” “MARI,” the “Company,”
        “we,” “our,” or “us”). MARI Group is a global live events company
        focused on sport, art, lifestyle, and entertainment, with a portfolio of
        brands and events and related digital platforms.
      </p>

      <h2>What is this Privacy Notice about?</h2>

      <p>
        This Privacy Notice (“Notice”) describes how we use the personal data we
        collect from and about you.
      </p>

      <h2>Definitions</h2>

      <p>
        For purposes of this Notice, the following terms have the meanings set
        out below. Where a jurisdiction's law defines a term differently, the
        local definition governs with respect to data subject to that
        jurisdiction's law. These terms are used as defined below regardless of
        capitalization.
      </p>

      <p>
        <strong>Personal Data</strong> means any information relating to an
        identified or identifiable natural person.
      </p>

      <p>
        <strong>Identifiable</strong> means the ability to determine a specific
        natural person, directly or indirectly, by reference to one or more data
        elements, as a name, identification number, location data, online
        identifier, or factors specific to that person's physical,
        physiological, genetic, mental, economic, cultural, or social identity.
      </p>

      <p>
        <strong>Sensitive Personal Data</strong> is a subset of personal data
        and means categories of personal data that are subject to heightened
        legal protection under applicable law, which may include (depending on
        jurisdiction) data revealing racial or ethnic origin, health data,
        genetic or biometric data, criminal history data, data concerning a
        person's sex life or sexual orientation, trade union membership,
        religious or philosophical beliefs, or political opinions, as well as
        other categories designated as sensitive, special, or similar under
        local law (such as government identification numbers, financial account
        information, or precise geolocation data).
      </p>

      <p>
        <strong>Process</strong> (regardless of verb tense) means any operation
        or set of operations performed on personal data, whether or not by
        automated means, including collection, recording, organization,
        structuring, storage, adaptation or alteration, retrieval, consultation,
        use, disclosure by transmission, dissemination or otherwise making
        available, alignment or combination, restriction, erasure, or
        destruction.
      </p>

      <h2>Who does this notice cover?</h2>

      <p>This Notice covers:</p>

      <ul>
        <li>
          visitors to the MARI website
          (<a [routerLink]="['/']">www.marigrp.com</a>);
        </li>
        <li>contractors to MARI group entities; and</li>
        <li>
          all others not covered by one of the privacy notices listed below,
          such as personnel of suppliers, vendors, business partners (such as
          event sponsors and organizers), and customers.
        </li>
      </ul>

      <p>There are separate notices for these groups:</p>

      <ul>
        <li>
          For job applicants and job prospects – Job Applicant Privacy Notice;
        </li>
        <li>
          For employees –
          <a [routerLink]="['/employee-privacy-notice']">Employee Privacy
          Notice</a>; and
        </li>
        <li>
          For customers, such as event attendees – locate the privacy notice
          posted on the website for the event, service, or product.
        </li>
      </ul>

      <h2>What personal data might we collect?</h2>

      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>Website visitors</th>
              <th>Contractors (outsourced labor)</th>
              <th>
                Personnel of suppliers, vendors, and other business partners
              </th>
              <th>Personnel of business customers</th>
              <th>Office visitors and guests</th>
              <th>Investors</th>
              <th>
                Dependents, beneficiaries, employee family, and emergency
                contacts
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Name, including given name, family name, middle name or initial,
                and any suffix or salutation
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Work contact information, such as work email, work telephone,
                mailing address, work office location
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Employer information (or the company you represent), including
                company name, location, address, and country of incorporation
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>Occupation, job title, function, and department</td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Organizational chart information, such as supervisor, assistant,
                and reporting chain
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Information required to issue a badge and/or provide office
                access, such as a photo and office access information
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>X</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Work experience, educational history, data contained in a resume
                or CV, skills, licenses, certifications, authorization to
                perform a certain job
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Membership in a trade association or professional organization
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Work assignments and work completed, including work product
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Payment information, including invoices and information required
                to provide payment, such as direct deposit or credit card
                information
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Time collection information, such as hours worked and services
                provided
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Event registration information, including attendance, food
                preferences, travel arrangements, and assistance that may be
                required
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Home contact information, including home address and home
                telephone number
              </td>
              <td></td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Personal contact information, including mobile telephone and
                personal email
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Contact information as provided, whether personal, home, work or
                a combination
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>Training information</td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                MARI computer, network, and communications information and logs,
                including username/login credentials, passwords, and information
                you store, send, submit, or receive through MARI networks and
                systems
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                User authentication information, such as user ID, password and
                security questions
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td>X</td>
            </tr>
            <tr>
              <td>Charitable gift and donation information</td>
              <td></td>
              <td></td>
              <td>X</td>
              <td></td>
              <td></td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Information regarding health and injuries, such as reports of
                onsite injuries and work-related reported health issues
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Communications with or by you, such as emails that you send, or
                information you may post in a public space about MARI
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Online activity, including browsing history, search history,
                video viewing history, interactions with our Site, email, or
                advertisement, IP address, general location (such as country or
                area) based on IP address
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>Language and communication preferences</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>Survey responses</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Video or other recording, such as collected during an event, a
                recorded meeting, or through CCTV
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Information that may be used for sanctions and other screening,
                such as screening matches, date of birth, and country of
                residence
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Information necessary for the provision of benefits as a
                dependent or beneficiary, such as health data, Social Security
                Number, and claim information
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Government-issued identification number, such as a tax
                identification number, passport number, or driver’s license
                number
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Conflict of interest information, such as relationships to
                company employees
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Background check information, including criminal history
                screening where permitted by applicable law
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Visitor information, including the time, date and location of
                visits, the purpose for the visit, and vehicle information
                necessary for parking
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Information regarding gifts that may have been given or received
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Location information for devices that provide location-based
                services, such as conference room technology and printers
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Website and application data, such as IP address, log files, and
                browsing history
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>Social media information</td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Emergency contact information, which may include information
                about non-employees, such as family members or friends you
                identify as an emergency contact
              </td>
              <td></td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>Allegations of misconduct as may be reported</td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Information you voluntarily share in MARI systems, such as a
                nickname, photographs, and interests
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Other information that you may voluntarily share, such feedback
                or comments or any other information that you choose to share
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Except as listed above, we do not collect sensitive personal data from
        you, unless you voluntarily provide that information to us. We do not
        intentionally process personal data from children (those who are younger
        than 18 years of age), except for dependent and beneficiary information,
        which is shared voluntarily with us by a parent or legal guardian. If we
        discover that we have inadvertently collected personal data from a child
        under 18 years of age, we will promptly take all reasonable measures to
        delete such information from our systems.
      </p>

      <p>
        We may collect deidentified or pseudonymized information from you that
        may not by itself reasonably identify you as the source of the
        information (“Deidentified Information”). This Deidentified Information
        is collected when you navigate our website and may include:
      </p>

      <ul>
        <li>device type;</li>
        <li>device operating system;</li>
        <li>internet browser type;</li>
        <li>internet service provider;</li>
        <li>referring/exit pages;</li>
        <li>date/time stamp; and</li>
        <li>clickstream information.</li>
      </ul>

      <p>
        We will take reasonable measures to ensure that Deidentified Information
        we collect is not reasonably identifiable and may not later be easily
        used to identify you.
      </p>

      <h2>How do we collect your personal data?</h2>

      <p>
        Generally, we will obtain your personal data directly from you. We may,
        however, obtain information from your employer or the company you
        represent. We may also seek information from third parties, such as if
        we are required to conduct sanctions screening. We may also process
        publicly available information about you, such as a profile on a
        professional website or information you post about a MARI company on a
        social media site.
      </p>

      <h2>For what purposes do we use your personal data?</h2>

      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>Website visitors</th>
              <th>Contractors (outsourced labor)</th>
              <th>
                Personnel of suppliers, vendors, and other business partners
              </th>
              <th>Personnel of business customers</th>
              <th>Office visitors and guests</th>
              <th>Investors</th>
              <th>
                Dependents, beneficiaries, employee family, and emergency
                contacts
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Managing communications with you, including responding to your
                messages
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Managing work assignments, supervising assignments, paying for
                work performed or obtaining payment for work, expense
                reimbursement
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Conducting business operations, such as managing our events,
                business planning, and financial administration
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Producing our events, including providing tickets, managing
                entrance to the events, and ensuring a positive event experience
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>
                Facilitate your attendance at and participation in our events,
                including sending you reminders and information
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
            </tr>
            <tr>
              <td>Personalize your experience</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Designing, creating, and producing our events, services and
                products
              </td>
              <td></td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>Marketing our events, services, and products</td>
              <td></td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Assisting our customers and business partners with the promotion
                of their events, services and products
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Providing training and/or ensuring that personnel are properly
                certified
              </td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>Health, safety, and personal security</td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td>
                Cybersecurity, including ensuring the integrity of our networks
                and systems
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>Providing and managing benefits</td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>X</td>
            </tr>
            <tr>
              <td>Reporting and statistical analysis</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>Ensuring compliance with applicable law</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Reviewing and reporting any conflicts of interest or related
                party transactions
              </td>
              <td></td>
              <td></td>
              <td>X</td>
              <td>X</td>
              <td></td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Implementing our compliance programs, including audits and
                self-assessments
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Responding to reported allegations and/or conducting internal
                investigations
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Responding to government inquiries and reporting as may be
                required by law
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>Prosecuting and defending legal claims</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>Protecting intellectual property rights</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Managing business transactions, such as acquisitions,
                divestitures, mergers, and other transactions that may involve
                the purchase or sale of some or all of one or more of a MARI
                company’s assets
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>
                Detect, investigate, and prevent fraud, security incidents, and
                other harmful or unlawful activity
              </td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
            <tr>
              <td>As may otherwise be required by law</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
              <td>X</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        For these purposes, MARI may use any of the personal data it processes,
        as appropriate for the purpose.
      </p>

      <p>The legal basis for processing is:</p>

      <ul>
        <li>as necessary to perform a contract, to the extent applicable;</li>
        <li>as required by local legal obligations; and</li>
        <li>in furtherance of a legitimate business interest.</li>
      </ul>

      <p>
        On limited occasions, we may rely on your consent for processing, and,
        in such circumstances, we will seek your explicit consent. To the extent
        we seek your consent, you are able to withdraw that consent by
        contacting us as indicated at the bottom of this Notice. Note that your
        withdrawal of your consent is only for future processing and it may have
        consequences, such as your inability to participate in certain
        opportunities.
      </p>

      <h2>Where do we store your personal data?</h2>

      <p>
        Because MARI operates across multiple countries, we may transfer your
        information between MARI entities or across borders to accomplish the
        purposes described above, including transfers to service providers in
        other countries. The personal data that we collect from you may be
        transferred to and stored at a destination outside the one in which you
        reside or in which you have shared the data with us. The jurisdictions
        to which we may transfer your data include the United States and other
        locations where we host events. These jurisdictions may not have
        equivalent data protection laws to those applicable to your
        jurisdiction, such as the UK or European Economic Area (EEA).
      </p>

      <p>
        If we transfer personal data to another jurisdiction, we will do so
        consistently with applicable law and only to the extent necessary for
        the purposes listed above. We will also confirm that the transfer is
        authorized by an available mechanism to allow for legal transfer and
        ensure an adequate level of protection, as may be required by law. Our
        standard practice is to use standard contractual clauses approved by the
        European Commission and the UK Information Commissioner’s Office to
        facilitate such data transfers. If you have any questions about our data
        transfer practices, please contact us as described at the end of this
        Notice.
      </p>

      <h2>To whom might we disclose your personal data?</h2>

      <p>
        MARI limits access to personal data on an as-needed basis. Personal data
        is used by and shared among MARI's operating companies, subsidiaries,
        divisions, and event properties worldwide for the purposes identified
        above. We may also share your business contact information with
        customers, venues, event partners, and business partners to support
        regular operations.
      </p>

      <p>
        MARI may also share personal data with third parties on an as-needed
        basis, including trusted service providers, consultants, and contractors
        granted access to MARI facilities or systems, and with government
        agencies as required by law. MARI will only share personal data outside
        the MARI group of companies to:
      </p>

      <ul>
        <li>
          Engage service providers. MARI has retained to perform services on our
          behalf, such as HR information systems, payroll providers, benefits
          administrators, training providers, professional development
          providers, and recruiting services. These providers are contractually
          restricted from using or disclosing information except to perform
          services for MARI or comply with legal requirements.
        </li>
        <li>
          Facilitate our events by sharing with event organizers, hosts, and
          sponsors. Some of our events are owned by MARI and other events are
          produced by us working with one or more partners. Our events may also
          be hosted at a third party’s location, such as a park owned by a
          government entity. Where an event includes a named sponsor, we may
          share some marketing and other information about you with that sponsor
          so that you can attend or participate in the event. Where we are
          managing the event that is owned by someone else, your personal data
          may be collected and used by us to produce the event. We may also need
          to share information with the site host for safety, security, and
          other legal reasons.
        </li>
        <li>
          Comply with legal obligations, such as tax and regulatory obligations,
          and respond to court proceedings or legitimate law enforcement
          requests.
        </li>
        <li>
          Investigate suspected or actual legal, policy, or compliance
          violations.
        </li>
        <li>Prevent physical harm and protect safety.</li>
        <li>
          Support the acquisition, sale or transfer of all or a portion of
          MARI’s business or assets.
        </li>
      </ul>

      <h2>How do we protect personal data?</h2>

      <p>
        The security and confidentiality of your personal data is very important
        to us. We use commercially reasonable security measures to protect your
        personal data. We use a variety of administrative, physical, and
        technological solutions to protect your information from unauthorized
        access, loss, or misuse. Because there is always some risk in
        transmitting information over the internet and otherwise processing
        information, we cannot guarantee or warrant that your personal data will
        be completely secure from theft by hackers or from other criminal
        actors, or in the event of a system or network compromise.
      </p>

      <h2>How long do we retain personal data?</h2>

      <p>
        MARI retains personal data for the length of purposes for which it was
        collected and any additional period required by applicable law,
        regulation, court or administrative proceedings, or audit requirements.
      </p>

      <h2>What are your legal rights and choices about your personal data?</h2>

      <p>
        If you ever wish to access, update, change, delete, opt-out of us
        sharing, or otherwise control your personal data, you may do so by
        contacting us using one of the methods in the “How can you contact us?”
        section. To help us process your request, please provide sufficient
        information to allow us to identify you in our records. We reserve the
        right to ask for additional information verifying your identity prior to
        disclosing any personal data to you. Should we ask for verification, we
        will only ask to confirm personal data that we already have about you
        and the information you provide will be used only for verification
        purposes. We will make commercially reasonable efforts to respond to
        opt-out requests and handle requests to access, update, change, or
        delete personal data without unreasonable delay.
      </p>

      <p>
        <strong>Residents of any EU country or country with a GDPR-style law
        (such as Switzerland):</strong> You have the right to request access to,
        correction of, or erasure of your personal data; to seek restriction of
        or object to processing of certain personal data; and to seek data
        portability in certain circumstances. To make such a request, use the
        contact methods at the end of this Notice. You also have the right to
        lodge a complaint with your national or state data protection authority
        (supervisory authority). You can find contact information for the EU
        data protection authorities at
        <a href="https://www.edpb.europa.eu/about-edpb/our-members_en" target="_blank" rel="noopener noreferrer">https://www.edpb.europa.eu/about-edpb/our-members_en</a>.
      </p>

      <p>
        <strong>UK residents:</strong> If you have a complaint about how MARI
        has handled your personal data, you may submit a complaint using the
        contact methods below. We will endeavor to acknowledge receipt within 30
        days, may request additional information to verify your identity or
        clarify the scope of your complaint, and will keep you updated on the
        investigation and its outcome without undue delay. If you are not
        satisfied with the outcome, you have the right to complain to the
        Information Commissioner's Office (ICO), which can be contacted at
        <a href="https://ico.org.uk/" target="_blank" rel="noopener noreferrer">https://ico.org.uk/</a>.
      </p>

      <p>
        <strong>Residents of the United States:</strong> MARI collects Social
        Security Numbers where required by law, such as for tax and payroll
        purposes, and will take appropriate care to protect confidentiality,
        limit collection, restrict access on a need-to-know basis, apply
        appropriate technical safeguards, and ensure proper disposal.
      </p>

      <p>
        If you are a resident of certain states in the United States, you have
        the following rights:
      </p>

      <ul>
        <li>
          Right to know about the personal data we have collected about you and
          how it is used and shared;
        </li>
        <li>
          Right to delete personal data collected from you (with some
          exceptions);
        </li>
        <li>Right to opt-out of the sale or sharing of your personal data;</li>
        <li>
          Right to not be discriminated against for exercising your privacy
          rights;
        </li>
        <li>
          Right to correct inaccurate personal data if we have incorrect
          personal data about you; and
        </li>
        <li>
          The right to limit the use and disclosure of sensitive personal data
          collected.
        </li>
      </ul>

      <p>
        To exercise your rights, please use one of the methods identified at the
        bottom of this Notice. Please provide your name, a way for MARI to
        contact you, information about your relationship with MARI, and details
        of the action you would like MARI to take. MARI will investigate and,
        where necessary, verify your identity before responding. Be aware that
        your rights (including those enumerated elsewhere in this Notice) may be
        limited by other applicable legal rights and obligations.
      </p>

      <p>
        <strong>Residents of California:</strong> California residents may
        annually request information shared with other businesses for their own
        direct marketing use in the prior calendar year. MARI does not share
        personal data with other businesses for their own direct marketing
        purposes.
      </p>

      <p>
        <strong>Residents of Australia:</strong> Each MARI entity in Australia
        is an "APP entity" regulated under the Privacy Act 1988 (Cth)
        ("Australian Privacy Act"). References to MARI in this Notice extend to
        Australian subsidiaries with respect to their collection, handling, and
        disclosure of personal data about Australian residents. Where an
        Australian MARI subsidiary discloses personal data to an overseas
        recipient for reasons described in this Notice, it will take reasonable
        steps to ensure the overseas recipient does not breach the Australian
        Privacy Act.
      </p>

      <p>
        Australian residents may email
        <a href="mailto:dataprivacy@marigrp.com">dataprivacy@marigrp.com</a>
        to access, correct, or update their personal data, ask questions about
        MARI's privacy practices, or make a complaint. If MARI takes more than
        30 days to respond, or if you are dissatisfied with the outcome, you may
        complain to the Office of the Australian Information Commissioner
        (OAIC), which can be contacted at
        <a href="https://www.oaic.gov.au/" target="_blank" rel="noopener noreferrer">https://www.oaic.gov.au/</a>.
      </p>

      <p>
        <strong>Residents of Hong Kong:</strong> MARI's Hong Kong operations are
        subject to the Personal Data (Privacy) Ordinance (PDPO). You have rights
        to request access to and correction of your personal data held by MARI.
        Requests may be submitted using the contact details below. If we are
        unable to resolve your concern, complaints regarding our handling of
        personal data may be raised with the Office of the Privacy Commissioner
        for Personal Data (PCPD), which can be contacted at
        <a href="https://www.pcpd.org.hk/" target="_blank" rel="noopener noreferrer">https://www.pcpd.org.hk/</a>.
      </p>

      <p>
        <strong>Residents of South Korea:</strong> MARI's South Korean
        operations are subject to the Personal Data Protection Act (PIPA). You
        have the right to request access to, correction of, deletion of, or
        suspension of processing of your personal data. Where required by PIPA,
        MARI will obtain separate consent for the collection or transfer of
        Sensitive Personal Data (as defined in PIPA as "sensitive information,"
        including health and criminal history data) and for cross-border
        transfers. If we are unable to resolve your concern, complaints may be
        directed to the Personal Data Protection Commission (PIPC), which can be
        contacted at
        <a href="https://pipc.go.kr/eng/index.do" target="_blank" rel="noopener noreferrer">https://pipc.go.kr/eng/index.do</a>.
      </p>

      <p>
        <strong>Residents of the United Arab Emirates:</strong> MARI's UAE
        operations are subject to applicable UAE data protection law, including
        Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data
        (PDPL) and, where applicable, free-zone data protection regimes (e.g.,
        DIFC or ADGM, if relevant to your employing entity). You may have rights
        to access, correct, or request erasure of your personal data, subject to
        applicable exceptions. Requests may be submitted using the contact
        information at the bottom of this Notice.
      </p>

      <h2>How do we use cookies or other tracking technologies?</h2>

      <p>
        A cookie is a small data file that is transferred to an internet
        browser, which enables our website to remember and customize your
        subsequent visits. Additionally, we may use web beacons (also called
        tracking pixels), which are single-pixel, electronic images embedded in
        our website that allow us to gather information about your browsing
        activities on our website. We use cookies and web beacons to enhance
        your browsing experience, serve personalized content, and analyze our
        traffic. You can choose which types of cookies to allow in the banner on
        our website.
      </p>

      <p>
        <strong>Global Privacy Controls.</strong> We recognize user-initiated
        opt-out preference signals sent by your web browser, such as the global
        privacy control (“GPC”), as a valid request to opt-out of the sale and
        sharing of personal data processed on this website (i.e., through
        cookies and tracking technologies). For more information about GPC and
        how to implement it on your browser, please visit
        globalprivacycontrols.org.
      </p>

      <p>
        <strong>Do Not Track.</strong> Please note that certain web browsers may
        also transmit “do-not-track” signals to the websites with which the user
        communicates. Because there currently is no industry standard concerning
        what, if anything, websites should do when they receive such signals, we
        do not alter our practices when the website receives a “do-not-track”
        signal from a visitor’s browser.
      </p>

      <h2>What should you know about third party links?</h2>

      <p>
        Our website, newsletters, email updates and other communications may
        contain links to other websites or applications (“Linked Sites”),
        including our partner networks, retail partners, advertisers and
        affiliates. These Linked Sites are not owned or controlled by us. We do
        not control the collection or use of any information, including personal
        data, which occurs while you visit Linked Sites. If you follow a link to
        any of these websites, please note that the Linked Sites likely have
        their own privacy notices and that we do not accept any responsibility
        or liability for these notices. Therefore, we make no representations or
        warranties for — and will not in any way be liable for — any content,
        products, services, software, or other materials available on Linked
        Sites.
      </p>

      <p>
        You may be able to use social sharing features and other integrated
        tools on our website such as the Facebook "Like" button. These features
        and tools may permit you to share actions you take on our website with
        other media, and vice versa. Your use of such features enables the
        sharing of information with your friends or the public, depending on the
        settings you establish with the service that provides the social sharing
        feature. Please remember that use of the information you share on social
        media sites will be governed by the privacy notices of the social media
        provider, and we are not responsible for how they treat the information.
      </p>

      <h2>What obligations do you have?</h2>

      <p>
        <strong>Individuals who provide information about family members and
        others:</strong> For emergency contact purposes and other purposes, you
        may choose to provide MARI with information about family members and
        others connected to you. Before providing that information, you must
        ensure you have the legal authority to do so, including obtaining
        consent, if that is required under applicable law. If you provide
        information as the legal representative of a minor, your choice to
        provide it represents consent that MARI may collect, process, and
        transfer that information for the purposes set out in this Notice.
      </p>

      <h2>How will we keep this notice up to date?</h2>

      <p>
        We reserve the right to update this Notice from time-to-time in our sole
        discretion. If our privacy practices change materially in the future, we
        will post an updated version of this Notice to our website. It is your
        responsibility to review this Notice for any changes each time you use
        our website. We will not lessen your rights under this Notice without
        your explicit consent. If you do not agree with the changes made, we
        will honor any opt-out requests made after the effective date of a new
        privacy notice.
      </p>

      <h2>What is the effective date of this notice?</h2>

      <p>
        This Notice was last modified as of the effective date printed at the
        top. This Notice replaces and supersedes any prior applicable privacy
        notices, including ones that may have been issued by predecessor owners
        of any MARI entities.
      </p>

      <h2>How can you contact us?</h2>

      <p>
        You can exercise your data privacy (data protection) rights (make a
        “Request”), ask a question about this Notice, inquire about our privacy
        compliance program, or ask any other question about your personal data
        by:
      </p>

      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>Contact</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Webform</td>
              <td>
                <a href="https://privacyportal.onetrust.com/webform/889c435d-64b4-46d8-ad05-06332fe1d097/132dec60-db4a-433d-9873-32b729def610" target="_blank" rel="noopener noreferrer"><strong>click
                link here</strong></a>
              </td>
            </tr>
            <tr>
              <td>Unsubscribe</td>
              <td>
                To unsubscribe from any MARI newsletter, email subscription, or
                any marketing messages, you can use the unsubscribe feature at
                the bottom of the email or type “Stop” in response to a text
                message
              </td>
            </tr>
            <tr>
              <td>Email</td>
              <td>
                <a href="mailto:dataprivacy@marigrp.com">dataprivacy@marigrp.com</a>
              </td>
            </tr>
            <tr>
              <td>Mail</td>
              <td>
                AE EventsCo Holdings LLC<br />Attention:
                Privacy<br />8383 Wilshire Blvd, Suite
                400<br />Beverly Hills, CA 90211 US
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Only you, or someone legally authorized to act on your behalf, may make
        a Request related to your personal data. Making a Request does not
        require you to have or create an account with us. Some states impose a
        limit on the number of Requests you can make in a year.
      </p>

      <p>Your Request must:</p>

      <ul>
        <li>
          Provide sufficient information that allows us to reasonably verify you
          are the person about whom we collected personal data (or that person’s
          authorized representative); and
        </li>
        <li>
          Describe what you are seeking with sufficient detail that allows us to
          properly understand, evaluate, and respond to it.
        </li>
      </ul>

      <p>
        We cannot respond to your Request or provide you with personal data if
        we cannot verify your identity or authority to make the request and
        confirm the personal data relates to you. We will only use personal data
        provided in a Request to verify the requestor’s identity or authority to
        make the request and to process the Request.
      </p>

      <p>
        We will acknowledge receipt of a Request within ten (10) days. We
        endeavor to respond to Requests within thirty (30) days of receipt. If
        we require more time (up to ninety (90) days), we will inform you of the
        reason and extension period in writing. If we cannot comply with a
        Request in whole or in part, we will explain the reason. If we do not
        grant your request, you have the right to appeal to us directly, using
        any of the contact methods (other than the unsubscribe options). In your
        appeal, you must state the reason for your appeal and what you are
        seeking. If you appeal, we will provide a written reply within 60 days
        of receipt of your appeal.
      </p>

      <p>
        We do not charge a fee to process or respond to a Request, unless it is
        excessive, repetitive, or unreasonable. If we determine that the Request
        warrants a fee, we will tell you why we made that decision and provide
        you with a cost estimate before completing your Request.
      </p>

      <p>
        We may retain certain information for recordkeeping or to complete
        transactions initiated before your request.
      </p>
    </div>
  </div>
</section>
